import { useEffect, useRef, useState } from 'react';

export interface Attachment {
  id: string;
  category: string | null;
  contentType: string | null;
  filename: string;
}

export interface Message {
  id: string;
  authorId: string | null;
  body: string | null;
  createdAt: string | number;
  attachments?: Attachment[];
}

/** Servera notikumi (apps/api/src/services/chat-live.ts) — ligzda ir tikai klausīšanās. */
export type ChatLiveEvent =
  | { type: 'message'; message: Message }
  | { type: 'attachment'; messageId: string; attachment: Attachment };

export type ChatSocketStatus = 'idle' | 'connecting' | 'live' | 'reconnecting';

export interface ChatSocketHandlers {
  onEvent: (event: ChatLiveEvent) => void;
  /** Pēc atkārtota savienojuma — ielādēt pa REST to, kas pazuda pārtraukuma laikā. */
  onReconnect?: () => void;
}

const MAX_BACKOFF_MS = 30_000;

function socketUrl(threadId: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/chat/threads/${threadId}/ws`;
}

function parseEvent(data: unknown): ChatLiveEvent | null {
  if (typeof data !== 'string') return null;
  try {
    const parsed = JSON.parse(data) as Partial<ChatLiveEvent>;
    return parsed.type === 'message' || parsed.type === 'attachment'
      ? (parsed as ChatLiveEvent)
      : null;
  } catch {
    return null;
  }
}

/**
 * Dzīvā tērzēšana: viena ligzda uz aktīvo pavedienu, eksponenciāla atkārtošana (≤ 30 s),
 * aizvēršana pie pavediena maiņas/atmontēšanas. Sūtīšana paliek REST.
 */
export function useChatSocket(
  threadId: string | null,
  handlers: ChatSocketHandlers,
): ChatSocketStatus {
  const [status, setStatus] = useState<ChatSocketStatus>('idle');
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (!threadId) {
      setStatus('idle');
      return;
    }
    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let disposed = false;

    const connect = () => {
      setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
      const ws = new WebSocket(socketUrl(threadId));
      socket = ws;
      ws.onopen = () => {
        if (disposed) return;
        const reconnected = attempt > 0;
        attempt = 0;
        setStatus('live');
        if (reconnected) handlersRef.current.onReconnect?.();
      };
      ws.onmessage = (evt) => {
        if (disposed) return;
        const event = parseEvent(evt.data);
        if (event) handlersRef.current.onEvent(event);
      };
      ws.onclose = () => {
        if (disposed) return;
        setStatus('reconnecting');
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt);
        attempt += 1;
        timer = setTimeout(connect, delay);
      };
    };
    connect();

    return () => {
      disposed = true;
      clearTimeout(timer);
      socket?.close();
    };
  }, [threadId]);

  return status;
}
